// MUL-2333 (2026-09-08): HTS's actual sub-agreement payment-terms policy is
// "paid when paid" by default, with N30 / N45 / N60 only as situational overrides.
// 2026-10-08 (Morgan, MUL-3919): Net 45 restored at Mark Costa's request —
// subs may request it and approvers may approve/counter with it. Policy
// version msa-payment-terms-v2-2026-10-08.
// The 45-day option has been removed. `null` in `PaymentTermsDays` is the
// canonical representation of "paid when paid" (the standard); 30 and 60 are
// the only negotiable deviations. The MSA DocuSign envelope reads the
// wording out of `negotiable-terms.ts` and renders the corresponding §4
// clause (paid-when-paid for `null`, "thirty (30) days" / "sixty (60) days"
// for the deviations).

import { z } from 'zod';

export type NegotiatedPaymentTermsDays = 30 | 45 | 60;
export type PaymentTermsDays = NegotiatedPaymentTermsDays | null;
export type InsuranceTier = 'standard';

// `wording` is keyed by the non-null values, since TypeScript's `Record` does
// not allow `null` as a key. Terms whose `standard` is `null` also carry a
// `paidWhenPaidWording` string; the renderer picks one or the other.
export interface NegotiableTermDef<V extends string | number> {
  key: string;
  clauseRef: string;
  standard: V | null;
  allowed: ReadonlyArray<V>;
  label: string;
  helpText?: string;
  wording: Record<V, string>;
  paidWhenPaidWording?: string;
}

export const NEGOTIABLE_TERMS = {
  paymentTermsDays: {
    key: 'paymentTermsDays',
    clauseRef: 'MSA §4 Payment Terms',
    standard: null,
    allowed: [30, 45, 60] as const,
    label: 'Payment terms (paid when paid is HTS default; Net 30 / Net 45 / Net 60 are situational overrides)',
    helpText:
      'HTS default is paid when paid. Net 30, Net 45 and Net 60 are situational overrides only and require internal review.',
    wording: {
      30: 'thirty (30) days after Contractor receives payment from the Customer for the Work',
      45: 'forty-five (45) days after Contractor receives payment from the Customer for the Work',
      60: 'sixty (60) days after Contractor receives payment from the Customer for the Work',
    },
    // The §4 sentence reads:
    //   "...Contractor will make payment no later than {{PAYMENT_TERMS}}
    //    (or any shorter period required by law)."
    // Each value carries its own "receives payment from the Customer" phrase,
    // so the rendered clause says it exactly once (Morgan, 2026-10-16,
    // MUL-3919; the old template appended "after receipt of payment from the
    // Customer for work completed", which doubled it for paid when paid).
    paidWhenPaidWording:
      'the date Contractor receives payment from the Customer for the Work',
  },
  insuranceTier: {
    key: 'insuranceTier',
    clauseRef: 'MSA §17 Insurance',
    standard: 'standard' as const,
    allowed: ['standard'] as const,
    label: 'Insurance tier',
    helpText: 'Only the standard coverage tier is available at launch.',
    wording: { standard: 'standard' },
  },
} as const satisfies Record<string, NegotiableTermDef<string | number>>;

export type TermKey = keyof typeof NEGOTIABLE_TERMS;
export type TermValueMap = {
  [K in TermKey]:
    | (typeof NEGOTIABLE_TERMS)[K]['standard']
    | (typeof NEGOTIABLE_TERMS)[K]['allowed'][number];
};
export type RequestedTerms = Partial<TermValueMap>;

// MUL-3932/3933/3934 Phase 3 completion contract (2026-09-22) §1 re-exports
// this schema from `sub-agreement-status.ts`. Keep the schema co-located
// with the type so the allowed literals come from one place: a future
// change to the allowed list (N45? N90?) only edits
// `NEGOTIABLE_TERMS.paymentTermsDays.allowed` above AND this schema — and
// the literal-union output type stays in sync. Parsing `30` or `60` yields
// a number; parsing `null` or an explicit JSON null yields null.
export const PaymentTermsDaysSchema = z.union([
  z.literal(NEGOTIABLE_TERMS.paymentTermsDays.allowed[0]),
  z.literal(NEGOTIABLE_TERMS.paymentTermsDays.allowed[1]),
  z.literal(NEGOTIABLE_TERMS.paymentTermsDays.allowed[2]),
  z.null(),
]);

/**
 * True for a negotiated (non-standard) payment term: any value in
 * NEGOTIABLE_TERMS.paymentTermsDays.allowed. Use this instead of hard-coded
 * `=== 30 || === 60` checks — those silently dropped Net 45 (2026-10-08).
 */
export function isNegotiatedPaymentTermsDays(value: unknown): value is NegotiatedPaymentTermsDays {
  return (NEGOTIABLE_TERMS.paymentTermsDays.allowed as readonly unknown[]).includes(value);
}

/** Fill unspecified keys with their standard value. */
export function effectiveTerms(req: RequestedTerms): TermValueMap {
  return {
    paymentTermsDays: req.paymentTermsDays ?? NEGOTIABLE_TERMS.paymentTermsDays.standard,
    insuranceTier: req.insuranceTier ?? NEGOTIABLE_TERMS.insuranceTier.standard,
  };
}

/** Return keys whose effective value differs from the configured standard. */
export function deviatedKeys(req: RequestedTerms): TermKey[] {
  const effective = effectiveTerms(req);
  return (Object.keys(NEGOTIABLE_TERMS) as TermKey[]).filter(
    (key) => effective[key] !== NEGOTIABLE_TERMS[key].standard,
  );
}

/** Render the wording for a single term's effective value. */
export function renderWording<K extends TermKey>(
  key: K,
  value: TermValueMap[K],
): string {
  const def = NEGOTIABLE_TERMS[key] as NegotiableTermDef<string | number>;
  if (value === null) {
    if (def.paidWhenPaidWording) return def.paidWhenPaidWording;
    // Defensive: a null value should always have a paidWhenPaidWording, but
    // if a term def forgets one, fall back to a clear placeholder so we
    // surface the bug instead of emitting literal "null".
    return 'paid when paid (HTS default)';
  }
  return def.wording[value as unknown as string & number];
}
